"use client";

import { useEffect } from "react";
import Link from "next/link";
import {
  ArrowRight,
  ShieldCheck,
  Calculator,
  Users,
  Check,
  HeartPulse,
  Home,
  Stethoscope,
  BookOpen,
} from "lucide-react";
import { T, useTextLocale, tl } from "@/components/t";
import { Button, Card, Icon, Stack, Grid } from "@/design";
import {
  FinancePage,
  SectionHeader,
  Tint,
  Tile,
  StatGrid,
  RecoLink,
} from "@/components/finance/Finance";
import { track } from "@/lib/analytics";
import { logger } from "@/lib/logger";
import styles from "./PariV2.module.css";

export default function PariLandingPage() {
  const loc = useTextLocale();

  useEffect(() => {
    try {
      track("pari_view");
    } catch (e) {
      logger.error("pari.view_track", { error: String(e) });
    }
  }, []);

  const onCta = (cta: string) => {
    try {
      track("pari_cta", { cta });
    } catch (e) {
      logger.error("pari.cta_track", { error: String(e), cta });
    }
  };

  return (
    <FinancePage
      backHref="/finanzen"
      backLabel={<T de="Finanzwissen" en="Financial knowledge" />}
      eyebrow={<T de="Versicherungen" en="Insurance" />}
      title={
        <T
          de={<>Versicherung, die <span className={styles.accent}>dir</span> gehört.</>}
          en={<>Insurance that works for <span className={styles.accent}>you</span>.</>}
        />
      }
      lead={
        <T
          de="Wir vergleichen BU, Lebensversicherung und Hausrat nach Effektivkosten und Verrentungsfaktor, nicht nach Sternen oder Provision. Du zahlst kein Honorar, wir nehmen keine Vertriebs-Provision."
          en="We compare disability (BU), life insurance and contents cover by effective cost (RIY) and annuity factor, not by star ratings or commission. You pay no advisory fee; we earn no sales commission."
        />
      }
      wide
    >
      <Stack gap="8">
        {/* Trust strip + primary CTAs */}
        <Tint tone="brand">
          <Stack gap="4">
            <div className={styles.trustRow}>
              <span className={styles.trustItem}>
                <Icon icon={ShieldCheck} decorative size={14} />
                <T de="0 % Provision · §34d Honorarberater" en="0 % commission · §34d fee-only adviser" />
              </span>
              <span className={styles.trustItem}>
                <Icon icon={Check} decorative size={14} />
                <T de="0 € Honorar für Studierende" en="0 € advisory fee for students" />
              </span>
              <span className={styles.trustItem}>
                <Icon icon={Check} decorative size={14} />
                <T de="Open-Methodologie" en="Open methodology" />
              </span>
            </div>
            <div className={styles.ctaRow}>
              <Link href="/pari/leben" className={styles.ctaLink} onClick={() => onCta("leben")}>
                <Button variant="primary" size="lg" rightIcon={<Icon icon={ArrowRight} decorative size={18} />}>
                  <T de="Leben-Vergleich starten" en="Start life & pension comparison" />
                </Button>
              </Link>
              <Link href="/pari/bu" className={styles.ctaLink} onClick={() => onCta("bu")}>
                <Button variant="secondary" size="lg">
                  <T de="BU-Vergleich" en="BU comparison" />
                </Button>
              </Link>
            </div>
          </Stack>
        </Tint>

        {/* Student rate hero card */}
        <Card variant="hero" className={styles.studentCard}>
          <Stack gap="4">
            <div className={styles.eyebrow}>
              <T de="Studenten-Tarif" en="Student rate" />
            </div>
            <h2 className={styles.cardTitle}>
              <T de="LV1871 · 50 % in den ersten 6 Jahren" en="LV1871 · 50 % off for the first 6 years" />
            </h2>
            <p className={styles.cardLead}>
              <T
                de="Schließe als Studierende:r jetzt eine BU oder fondsgebundene Rentenversicherung ab und zahle für sechs Jahre nur die Hälfte. Die Gesundheitsprüfung ist heute günstiger als sie es jemals wieder sein wird."
                en="As a student, take out a BU (disability) or unit-linked pension now and pay only half the premium for six years. Your health assessment will never be cheaper than it is today."
              />
            </p>
            <StatGrid cols={2}>
              <Tile
                tone="brand"
                value="2.160 €"
                label={<T de="Bei 60 €/Mon BU" en="At 60 €/mo BU" />}
                sub={<T de="Ersparnis in 6 Jahren" en="saved over 6 years" />}
              />
              <Tile
                tone="brand"
                value="3.600 €"
                label={<T de="Bei 100 €/Mon RV" en="At 100 €/mo pension" />}
                sub={<T de="Ersparnis in 6 Jahren" en="saved over 6 years" />}
              />
            </StatGrid>
            <Link href="/pari/leben?student=1" className={styles.inlineLink} onClick={() => onCta("student")}>
              <T de="Mein Studenten-Tarif berechnen" en="Calculate my student rate" />
              <Icon icon={ArrowRight} decorative size={16} />
            </Link>
          </Stack>
        </Card>

        {/* Why Pari */}
        <section>
          <SectionHeader title={<T de="Warum Pari?" en="Why Pari?" />} />
          <Grid cols={[1, 1, 3]} gap="4">
            <Pillar
              icon={Calculator}
              title={tl(loc, "Ranking nach Effektivkosten", "Ranked by effective cost")}
              body={tl(
                loc,
                "Wir sortieren nach RIY (Reduction in Yield), Verrentungsfaktor und Bedingungs-Score. Nicht nach Preis-Sternen, nicht nach Provision.",
                "We sort by RIY (Reduction in Yield), annuity factor and policy-quality score, not by star ratings, not by commission.",
              )}
            />
            <Pillar
              icon={ShieldCheck}
              title={tl(loc, "0 % Provision", "0 % commission")}
              body={tl(
                loc,
                "Wir nehmen keine Abschluss-Provision von Versicherern. Honorarberatung statt Vertriebsdruck. Bei Studierenden auch ohne Honorar.",
                "We take no sales commission from insurers. Fee-only advice instead of sales pressure. For students: no advisory fee either.",
              )}
            />
            <Pillar
              icon={Users}
              title={tl(loc, "Berufsgruppen-Kollektiv", "Professional group collective")}
              body={tl(
                loc,
                "500+ Mediziner:innen, Ingenieur:innen oder Lehrer:innen verhandeln mit uns einen Kollektivvertrag. Keine andere Plattform kann das.",
                "500+ doctors, engineers or teachers negotiate a group contract with us. No other platform can do this.",
              )}
            />
          </Grid>
        </section>

        {/* What we cover */}
        <section>
          <SectionHeader
            title={<T de="Was wir abdecken" en="What we cover" />}
            hint={<T de="Vier Bereiche" en="Four areas" />}
          />
          <Stack gap="2">
            <RecoLink
              href="/pari/bu"
              icon={<Icon icon={HeartPulse} decorative />}
              iconTone="default"
              title={tl(loc, "Berufsunfähigkeit", "Disability insurance (BU)")}
              desc="LV1871, Alte Leipziger, Swiss Life, HDI, Continentale, Volkswohl Bund, Allianz, ERGO"
              onClick={() => onCta("cover_bu")}
            />
            <RecoLink
              href="/pari/leben"
              icon={<Icon icon={ShieldCheck} decorative />}
              iconTone="default"
              title={tl(loc, "Leben & Rente", "Life & pension")}
              desc={tl(
                loc,
                "Risiko-LV, fondsgebundene RV, Riester, Rürup",
                "Term life, unit-linked pension, Riester, Rürup",
              )}
              onClick={() => onCta("cover_leben")}
            />
            <RecoLink
              href="/pari/sach"
              icon={<Icon icon={Home} decorative />}
              iconTone="default"
              title={tl(loc, "Hausrat & PHV", "Contents & liability")}
              desc="Adam Riese, Helden, BavariaDirekt, Haftpflichtkasse, InterRisk, Domcura"
              onClick={() => onCta("cover_sach")}
            />
            <RecoLink
              href="/pkv-gkv"
              icon={<Icon icon={Stethoscope} decorative />}
              iconTone="default"
              title={tl(loc, "PKV vs GKV", "Private vs public health")}
              desc={tl(
                loc,
                "Lebenslange Beitragsprognose, Wechsel-Beratung mit §34d-Berater",
                "Lifetime premium forecast, switching advice with a §34d adviser",
              )}
              onClick={() => onCta("cover_kv")}
            />
          </Stack>
        </section>

        {/* Open methodology */}
        <Tint tone="stone">
          <Stack gap="3">
            <h2 className={styles.methodTitle}>
              <T de="Open-Methodologie" en="Open methodology" />
            </h2>
            <p className={styles.methodBody}>
              <T
                de="Wir veröffentlichen die Berechnungs-Formeln, die Bedingungs-Gewichtung und alle (vorhandenen oder eben nicht vorhandenen) Provisions-Erträge auf einer öffentlichen Wiki-Seite. Check24 kann das nicht, ihr Geschäftsmodell ist Provision. Wir können es, weil unseres es nicht ist."
                en="We publish our calculation formulas, policy-quality weightings, and all (present or absent) commission revenue on a public wiki page. Check24 cannot do this, their business model is commission. We can, because ours is not."
              />
            </p>
            <Link
              href="/pari/methodologie"
              className={styles.inlineLink}
              onClick={() => onCta("methodologie")}
            >
              <Icon icon={BookOpen} decorative size={16} />
              <T de="Methodik ansehen" en="View methodology" />
              <Icon icon={ArrowRight} decorative size={16} />
            </Link>
          </Stack>
        </Tint>
      </Stack>
    </FinancePage>
  );
}

function Pillar({
  icon,
  title,
  body,
}: {
  icon: typeof Calculator;
  title: string;
  body: string;
}) {
  return (
    <Card variant="default">
      <Stack gap="2">
        <span className={styles.pillarIcon}>
          <Icon icon={icon} decorative size={22} />
        </span>
        <h3 className={styles.pillarTitle}>{title}</h3>
        <p className={styles.pillarBody}>{body}</p>
      </Stack>
    </Card>
  );
}
